import { API_BASE } from './config'
import { getAuthToken, getRequiredAuthToken, isLoggedIn, parseApiError } from './auth'

export { isLoggedIn }

export interface StringStats {
  likeCount: number
  commentCount: number
}

export interface StringPublic {
  stringId: string
  userId: string
  authorUsername: string
  authorProfileImageUrl?: string | null
  content: string
  stats: StringStats
  likedByMe?: boolean | null
  createdAt: string
  updatedAt: string
}

export interface CommentPublic {
  commentId: string
  stringId: string
  userId: string
  authorUsername: string
  text: string
  createdAt: string
}

export interface LikeStatusResponse {
  liked: boolean
  likeCount: number
}

function authHeaders(): HeadersInit {
  const token = getRequiredAuthToken('Log in to post a string.')
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}

export async function createString(content: string): Promise<StringPublic> {
  const res = await fetch(`${API_BASE}/strings`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ content: content.trim() }),
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

function optionalAuthHeaders(): HeadersInit {
  const token = getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function listStrings(
  limit = 50,
  offset = 0,
): Promise<StringPublic[]> {
  const res = await fetch(
    `${API_BASE}/strings?limit=${limit}&offset=${offset}`,
    { headers: optionalAuthHeaders() },
  )
  if (!res.ok) throw new Error(await parseApiError(res, { clearTokenOn401: true }))
  return res.json()
}

export async function searchStrings(
  q: string,
  limit = 50,
): Promise<StringPublic[]> {
  const trimmed = q.trim()
  if (trimmed.length < 2) return []

  const res = await fetch(
    `${API_BASE}/strings/search?q=${encodeURIComponent(trimmed)}&limit=${limit}`,
    { headers: optionalAuthHeaders() },
  )
  if (!res.ok) throw new Error(await parseApiError(res, { clearTokenOn401: true }))
  return res.json()
}

export async function getString(stringId: string): Promise<StringPublic> {
  const res = await fetch(`${API_BASE}/strings/${encodeURIComponent(stringId)}`, {
    headers: optionalAuthHeaders(),
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function listComments(stringId: string): Promise<CommentPublic[]> {
  const res = await fetch(
    `${API_BASE}/strings/${encodeURIComponent(stringId)}/comments`,
  )
  if (!res.ok) throw new Error(await parseApiError(res))
  return res.json()
}

export async function addComment(
  stringId: string,
  text: string,
): Promise<CommentPublic> {
  const res = await fetch(
    `${API_BASE}/strings/${encodeURIComponent(stringId)}/comments`,
    {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ text: text.trim() }),
    },
  )
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function likeString(stringId: string): Promise<StringPublic> {
  const res = await fetch(
    `${API_BASE}/strings/${encodeURIComponent(stringId)}/like`,
    { method: 'PUT', headers: authHeaders() },
  )
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function unlikeString(stringId: string): Promise<StringPublic> {
  const res = await fetch(
    `${API_BASE}/strings/${encodeURIComponent(stringId)}/like`,
    { method: 'DELETE', headers: authHeaders() },
  )
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function getLikeStatus(
  stringId: string,
): Promise<LikeStatusResponse> {
  const res = await fetch(
    `${API_BASE}/strings/${encodeURIComponent(stringId)}/like`,
    { headers: authHeaders() },
  )
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function listMyStrings(limit = 50): Promise<StringPublic[]> {
  const res = await fetch(`${API_BASE}/users/me/strings?limit=${limit}`, {
    headers: authHeaders(),
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function listUserStrings(
  username: string,
  limit = 50,
): Promise<StringPublic[]> {
  const normalized = username.trim().replace(/^@/, '')
  const res = await fetch(
    `${API_BASE}/users/by-username/${encodeURIComponent(normalized)}/strings?limit=${limit}`,
    { headers: optionalAuthHeaders() },
  )
  if (!res.ok) throw new Error(await parseApiError(res, { clearTokenOn401: true }))
  return res.json()
}
